import { inject, injectable } from "inversify";
import { ICacheProvider } from "../../domain/interfaces/cache-provider.interface";
import { IErrorReporter } from "../../domain/interfaces/error-reporter.interface";
import { CachingPolicy } from "./caching-policy";
import { DI } from "../di";
import { Request } from "../request";
import { Response } from "../response";

@injectable()
export class Cache {

	constructor(
		@inject(DI.CACHE_PROVIDER) private cacheProvider: ICacheProvider,
		@inject(DI.CACHING_POLICY) private cachingPolicy: CachingPolicy,
		@inject(DI.ERROR_REPORTER) private errorReporter: IErrorReporter
	) {}

	public async build(request: Request, response: Response): Promise<any> {

		this.cachingPolicy.validate(request, response);
		const ttl = this.lowestDuration(response.cachingSpecification!.ttl);

		try {
			return await this.cacheProvider.set(request.url, response, ttl);
		} catch (error) {
			this.errorReporter.error("Building cache failed", error);
			throw error;
		}
	}

	public async has(request: Request): Promise<boolean> {

		return this.cacheProvider.has(request.url);
	}

	public async get(request: Request): Promise<Response> {

		try {
			return await this.cacheProvider.get(request.url) as Response;
		} catch (error) {
			this.errorReporter.error("Retrieving cache failed", error);
			throw error;
		}
	}

	/**
	 * Returns the lowest TTL value available with a maximum of 30 days (2592000s)
	 *
	 * @param ttl
	 */

	private lowestDuration(ttl): number {

		return Math.min(Number(ttl), 2592000);
	}
}
